import { useEffect, useState } from "react";
import SVG from "react-inlinesvg";
import CustomModal from "../../Components/Modals/CustomModal";
import packageJson from '../../../package.json';
import * as Yup from "yup";
import { useFormik } from "formik";
import { Link, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import { providerActions } from "../../Store/Provider/reducer";
import ForgotPasswordForm from "./Components/ForgotPasswordForm";
import { initiateResetPassword, resetUserPassword, userAccountCheck } from "../../Store/Provider/ProviderAPI";
import { toastError, toastSuccess } from "../../Methods/Helpers/ToastHelper";
import { tempActions } from "../../Store/TempStore/reducer";
import { selectTempState } from "../../Store/TempStore/selectors";
import { TempStore } from "../../Store/TempStore/types";
import { handleTempStates } from "../../Methods/Helpers/TempStateHelper";
import { APIResponseType } from "../../Services/Models/Async/AsyncModel";
import Loader from "../../Components/Helpers/Loaders/Loader";

type ResetPassword = {
    verificationCode: string;
    username: string;
    password: string;
    confirmPassword: string;
}

type SendCodeType = {
  username: string;
}

const sendCodeSchema = Yup.object().shape({
    username: Yup.string().email("Please enter valid email address").required("Email is required"),
});

const initialValuesEmail: SendCodeType = {
  username: ""
}
const ForgotPassword = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSetPassword, setIsSetPassword] = useState<boolean>(false);
  const tempState: TempStore = useAppSelector(selectTempState);

  const formik = useFormik({
    initialValues: initialValuesEmail,
    validationSchema: sendCodeSchema,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    onSubmit: (values) => {
      checkUser(values.username);
      // resendVerificationCode();
    }
  });

  const checkUser = async (email: string) => {
    try {
      setIsLoading(true);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { success, code, message }: any = await userAccountCheck(email);
      switch (message) {
        case "USER_NOT_CONFIRMED":
        case "FORCE_CHANGE_PASSWORD":
          toastError("Please, first confirm your email");
          setIsLoading(false);
          break;
        case "RESET_REQUIRED": resendVerificationCode()
          break;
        case "USER_CONFIRMED": resendVerificationCode()
          break;
        case "USER_NOT_REGISTERED":
          toastError("You are not registered, please contact Kiddo team or your management to onboard you at Kiddo Provider Portal");
          setIsLoading(false);
          break;
        default:
          toastError(message);
          setIsLoading(false);
      }
    } catch (error: any) {
      toastError(error.toString());
      setIsLoading(false);
    }
  }

  const resendVerificationCode = async () => {
    setIsLoading(true);
    await initiateResetPassword(formik.values.username).then((response: any) => {
      if(response.success){
        toastSuccess(response.message);
        !isSetPassword && setIsSetPassword(true);
      }else{
        if(response.code === "LimitExceededException"){
          toastError("Attempt limit exceeded, please try after some time.");
        }else{
          toastError(response.message);
        }
      }
      setIsLoading(false);
    }).catch((error: any) => {
      toastError(error.message);
      setIsLoading(false);
    });
  }

  useEffect(() => {
    if(tempState?.forgotPassword?.username){
      formik.setTouched({...formik.touched, username: true});
      formik.setValues({...formik.values, username: tempState?.forgotPassword?.username}, true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFieldChange = (e: React.ChangeEvent<any>) => {
    let tempStateHandler: any = handleTempStates("forgotPassword", {...tempState}, e.target.name, e.target.value);
    dispatch(tempStateHandler.actionType(tempStateHandler.key));
    formik.handleChange(e);
  }

  const handlePasswordPopupClose = () => {
    dispatch(providerActions.logout());
    formik.resetForm({});
    formik.setValues({...initialValuesEmail});
    setIsSetPassword(false);
  }

  const onSubmitPassword = async (values: ResetPassword) => {
    setIsLoading(true);
    if(formik.values?.username){
      await resetUserPassword({username: formik.values.username, password: values.password, verificationCode: values.verificationCode})
      .then((response: APIResponseType<any>) => {
        if(response.success){
          toastSuccess("Password updated successfully");
          handlePasswordPopupClose();
          dispatch(tempActions.removeFromTemp("forgotPassword"));
          navigate("/login");
        }else{
          toastError(response.message);
        }
        setIsLoading(false);
      }).catch((error: APIResponseType<any>)=>{
        toastError(error.message);
        setIsLoading(false);
      });
    }else{
      setIsLoading(false);
      toastError("Sorry we were unable to process your request, please try again.")
    }
  }

  return (
  <>
    <div className="main-wrapper onboarding-screens">
      <div className="cards-wrap card-success">
        <div className="card-body onboarding-model create-account">
          <div className="header">
            <div className="logo mb-4">
              <SVG
                className="header-logo"
                src="Assets/Images/Logos/kiddo.svg"
                width="108"
                height="32"
              />
            </div>
          </div>
          <h1 className="title-medium text-center">Reset Password</h1>
          <p className="header-text">
            {isLoading ? "Verifying email..." : `Please enter email to get verification code`}
          </p>
          <div className="card-inner create-account position-relative">
            <form 
            onSubmit={(e) => {
              e.preventDefault();
            formik.handleSubmit();
            return false;
            }}
            noValidate
            autoComplete="off">
              <div className="floating-input-container">
                <input
                  placeholder=" "
                  name="username"
                  className="form-control"
                  type="email"
                  value={formik.values.username}
                  onChange={(e)=>handleFieldChange(e)}
                  onBlur={formik.handleBlur}
                />
                <label className="float-label" htmlFor="">Email</label>
                {formik.touched.username && !formik.errors.username && (
                  <span className="tick-validate right-position">
                      <SVG
                      className="tick-icon"
                      src="Assets/Images/Icons/tick-icon.svg"
                      width="16"
                      height="16"
                      />
                  </span>
                  )
                }
                {formik.touched.username && formik.errors.username ? <label className="text-error">{formik.errors.username}</label> : ""}
              </div>
              <div className="form-group btn-wrap mt-4">
                <button type="submit" className={`btn ${!formik.isValid ? "btn-custom-disabled" : "btn-success"}`} disabled={!formik.isValid}>Send Code</button>
              </div>
            </form>
            <div className="text-center mt-2">
              <p className="theme-color">
                Back to {" "}
                <Link to={"/login"} className="theme-color">
                  Login?
                </Link>
              </p>
            </div>
            <div className="text-center mt-2">
              <p className="theme-color">
                &#169; Kiddo Health Inc. | Version: {packageJson.version}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    {isSetPassword && 
      <>
          <CustomModal
            show={isSetPassword}
            BodyContent={() =>
            <>
              <div className="header w-100 justify-content-center">
                <div className="text-center">
                  <div className="logo mb-4 text-center">
                    <SVG
                      className="header-logo"
                      src="Assets/Images/Logos/kiddo.svg"
                      width="108"
                      height="32"
                    />
                  </div>
                  <h1 className="title-medium text-center theme-color fs-5 mb-3">
                  Verification
                  </h1>
                  <p className="header-text mb-5">
                    {`Verification code has been sent to ${formik.values?.username}`}
                  </p>
                </div>
              </div>
              <ForgotPasswordForm
                isInitialSendCode={true}
                onSubmitForm={onSubmitPassword}
                onCloseCancel={handlePasswordPopupClose}
                username={formik.values?.username}
              />
            </>
          }
          customClass="verify-otp-padding"
        />
      </>
    }
    {isLoading && <Loader />}
  </>
  )
}

export default ForgotPassword;